/**
 * Device sync service — communicates with the Hono server /devices/ endpoints.
 *
 * Ported from v1. Used by useDeviceSync hook to:
 * 1. Register or update this device on login / app activation
 * 2. List all user devices (to detect if device already exists)
 * 3. Poll for force_logout status (remote logout from Devices settings)
 * 4. Update device status on sign-out
 *
 * All requests go through the authenticated apiRequest() client.
 */

import { apiRequest } from "@/shared/utils/api/apiClient"
import type { DbDevice } from "@/db/schema"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DeviceStatus = "active" | "inactive" | "logged_out" | "force_logout"

export interface DeviceStatusResponse {
  status: DeviceStatus
  deviceId: string
}

export interface DeviceListResponse {
  success: boolean
  devices: DbDevice[]
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Get the status of a specific device.
 * Returns null if the device is not found or the request fails.
 */
export async function checkDeviceStatus(
  deviceId: string,
): Promise<DeviceStatus | null> {
  try {
    const res = await apiRequest<DeviceStatusResponse>({
      path: `/devices/${encodeURIComponent(deviceId)}/status`,
      method: "GET",
    })
    if (res.ok && res.data) {
      return res.data.status
    }
    return null
  } catch {
    return null
  }
}

/**
 * List all devices for the current user.
 * Used to check if a device already exists and to get its full data.
 */
export async function listDevices(): Promise<DbDevice[]> {
  try {
    const res = await apiRequest<DeviceListResponse>({
      path: "/devices",
      method: "GET",
    })
    if (res.ok && res.data?.devices) {
      return res.data.devices
    }
    return []
  } catch {
    return []
  }
}

/**
 * Update the status of a specific device.
 */
export async function updateDeviceStatus(
  deviceId: string,
  status: DeviceStatus,
): Promise<boolean> {
  try {
    const res = await apiRequest({
      path: `/devices/${encodeURIComponent(deviceId)}/status`,
      method: "PATCH",
      body: { status },
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Update device status with retry logic.
 * Critical for force_logout — we must confirm the status was updated.
 */
export async function updateDeviceStatusWithRetry(
  deviceId: string,
  status: DeviceStatus,
  maxRetries = 3,
): Promise<boolean> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const success = await updateDeviceStatus(deviceId, status)
    if (success) return true

    // Wait 1s before retrying
    if (attempt < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }
  }
  return false
}

/**
 * Register or update a device — sends nested { device, isNew } to match v1.
 * Called on app startup and on Capacitor appStateChange → active.
 */
export async function syncDevice(
  deviceData: Partial<DbDevice>,
  isNew: boolean,
): Promise<boolean> {
  try {
    console.log(
      `[DeviceSync] syncDevice(${isNew ? "NEW" : "UPDATE"}) id=${deviceData.id}`,
    )
    const res = await apiRequest({
      path: "/devices/sync",
      method: "POST",
      body: { device: deviceData, isNew },
    })
    if (!res.ok) {
      console.warn(
        `[DeviceSync] syncDevice failed: ${res.status}`,
        res.data ?? "",
      )
    }
    return res.ok
  } catch (e) {
    console.warn("[DeviceSync] syncDevice exception:", e)
    return false
  }
}
